<template>
    <div class="formula-field-editor">
        <div class="editor-container">
            <div class="toolbar">
                <div class="insert-field">
                    <a-dropdown :trigger="['click']">
                        <a-button> <i class="material-icons">table_view</i> {{ $t('table.addFields') }} </a-button>
                        <a-menu slot="overlay">
                            <a-menu-item v-for="field in availableFields" :key="field._id" @click="insertField(field)">
                                <i class="material-icons">{{ getFieldIcon(field.fieldType) }}</i>
                                {{ field.name }}
                            </a-menu-item>
                        </a-menu>
                    </a-dropdown>
                </div>
                <div class="insert-function">
                    <a-dropdown :trigger="['click']">
                        <a-button> <i class="material-icons">functions</i> {{ $t('table.addFunctions') }} </a-button>
                        <a-menu slot="overlay" class="function-menu">
                            <a-sub-menu
                                v-for="category in functionCategories"
                                :key="category.key"
                                :title="category.label"
                            >
                                <a-menu-item
                                    v-for="func in category.functions"
                                    :key="func.name"
                                    @click="insertFunction(func)"
                                >
                                    <span class="function-item">
                                        <span class="function-name">{{ func.name }}</span>
                                        <span class="function-desc">{{ func.description }}</span>
                                    </span>
                                </a-menu-item>
                            </a-sub-menu>
                        </a-menu>
                    </a-dropdown>
                </div>
                <div class="ai-assistant">
                    <SupsisButton class="ai-suggest-button" @click="showAiAssistant"  ghost>
                        <img src="/static/img/copilot.png" class="copilot-icon" />
                        {{ $t('table.createFormulaWithAssistant') }}
                    </SupsisButton>
                </div>
            </div>
            <div class="editor">
                <div class="formula-input-container" ref="editorContainer" @keydown="handleKeyDown" @click="setFocus">
                    <div
                        ref="editorInput"
                        class="formula-input"
                        contenteditable="true"
                        spellcheck="false"
                        @input="handleEditorInput"
                        @keyup="handleKeyUp"
                        @blur="handleBlur"
                        @paste="handlePaste"
                    ></div>
                </div>
                <div v-if="showAutocomplete" class="autocomplete-dropdown" :style="autocompletePosition">
                    <div class="autocomplete-title">{{ autocompleteTitle }}</div>
                    <div class="autocomplete-items">
                        <div
                            v-for="(item, index) in autocompleteItems"
                            :key="index"
                            class="autocomplete-item"
                            :class="{ active: autocompleteSelectedIndex === index }"
                            @click="insertAutocompleteItem(item)"
                        >
                            <template v-if="autocompleteType === 'function'">
                                <i class="material-icons">functions</i>
                                <div class="autocomplete-item-details">
                                    <div class="item-name">{{ item.name }}</div>
                                    <div class="item-description">{{ item.description }}</div>
                                    <div v-if="item.syntax" class="item-syntax">{{ item.syntax }}</div>
                                </div>
                            </template>
                            <template v-else-if="autocompleteType === 'field'">
                                <i class="material-icons">{{ getFieldIcon(item.fieldType) }}</i>
                                <div class="autocomplete-item-details">
                                    <div class="item-name">{{ item.name }}</div>
                                    <div class="item-description">{{ getFieldTypeLabel(item.fieldType) }}</div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            <div class="syntax-help">
                <p v-if="error" class="error-message">{{ error }}</p>
                <p v-else-if="expression" class="preview-message">
                    <span class="preview-label">Önizleme:</span>
                    <span class="highlighted-expression" v-html="escapedHighlightedExpression"></span>
                </p>
                <p v-else class="help-message">
                    Formül oluşturmak için alan ve fonksiyonları ekleyin. Formüller, değerleri diğer alanlardan hesaplar
                    ve otomatik olarak güncellenir.
                </p>
            </div>
        </div>
        <div class="examples-section">
            <a-collapse>
                <a-collapse-panel header="Formül Örnekleri" key="1">
                    <div class="examples">
                        <div class="example" v-for="(example, index) in formulaExamples" :key="index">
                            <div class="example-header">
                                <span class="example-name">{{ example.name }}</span>
                                <a-button size="small" @click="useExample(example.formula)">
                                    <i class="material-icons">content_copy</i> Kullan
                                </a-button>
                            </div>
                            <div
                                class="example-formula"
                                v-html="renderSafeHTML(highlightSyntax(example.formula))"
                            ></div>
                            <div class="example-description">{{ example.description }}</div>
                        </div>
                    </div>
                </a-collapse-panel>
            </a-collapse>
        </div>
        <Modal2
            v-if="aiAssistantVisible"
            title="Supsis Asistan: Formül Oluşturucu"
            @cancel="aiAssistantVisible = false"
            class="voice-agent-modal"
            :showFooter="false"
        >
            <FormulaGenerator
                :tableId="tableId"
                @formula-generated="useGeneratedFormula"
            />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import { functionCategories } from "../../../../../utils/formula-functions";
import CoPilot from "../../../../../utils/CoPilot";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import FormulaGenerator from "./formulaEditor/FormulaGenerator.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";

export default Vue.extend({
    name: "FormulaFieldEditor",
    components: {
        SupsisButton,
        FormulaGenerator,
        Modal2
    },
    props: {
        value: {
            type: String,
            default: "",
        },
        tableId: {
            type: String,
            required: true,
        },
        allFields: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            expression: this.value,
            error: null,
            cursorPosition: 0,
            editorContent: "",
            functionCategories,
            // Autocomplete properties
            showAutocomplete: false,
            autocompleteType: null, // 'function' veya 'field'
            autocompleteItems: [],
            autocompletePosition: { top: 0, left: 0 },
            autocompleteSelectedIndex: 0,
            autocompleteTitle: "",
            lastWord: "",

            formulaExamples: [
                {
                    name: "Toplam Tutar Hesaplama",
                    formula: "SUM({field_id_1}, {field_id_2})",
                    description: "İki sayısal alanın toplamını hesaplar",
                },
                {
                    name: "KDV Hesaplama",
                    formula: "ROUND({field_id_1} * 0.18, 2)",
                    description: "Bir alanın %18 KDV değerini hesaplar ve 2 ondalık basamağa yuvarlar",
                },
                {
                    name: "Tam Ad Oluşturma",
                    formula: 'CONCATENATE({field_id_1}, " ", {field_id_2})',
                    description: "Ad ve soyad alanlarını birleştirerek tam ad oluşturur",
                },
                {
                    name: "Koşullu Hesaplama",
                    formula: 'IF({field_id_1} > 1000, "Yüksek", "Düşük")',
                    description: 'Bir değer 1000\'den büyükse "Yüksek", değilse "Düşük" döndürür',
                },
                {
                    name: "Yaş Hesaplama",
                    formula: "YEAR(TODAY()) - YEAR({field_id_1})",
                    description: "Doğum tarihinden şu anki yaşı hesaplar",
                },
            ],
            allFunctions: [],
            // AI Assistant
            aiAssistantVisible: false,
            customerPrompt: "",
            aiLoading: false,
            aiResult: null,
            examplePrompts: [
                "İki sayısal alanın toplamını hesapla ve sonucu 2 ondalık basamağa yuvarla",
                "Fiyat alanını miktarla çarpıp, %18 KDV ekleyerek toplam tutarı hesapla",
                "Ad ve soyad alanlarını birleştirip tam isim oluştur",
                "Eğer satış tutarı 1000'den büyükse 'Yüksek', değilse 'Düşük' olarak değerlendir",
                "Doğum tarihi alanından yaşı hesapla",
                "Stok miktarı 10'dan az ise 'Kritik Stok', 10-50 arası ise 'Normal Stok', 50'den fazla ise 'Yüksek Stok' olarak göster",
                "Başlangıç tarihi ve bitiş tarihi arasındaki gün sayısını hesapla",
            ],
        };
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
        }),
        availableFields() {
            const filterType = (field) => {
                return ["string", "number", "date", "boolean", "array", "mixed"].includes(field.valueType);
            };

            return this.allFields
                .filter((field) => field.fieldType !== "formula" && filterType(field))
                .map((field) => ({
                    ...field,
                    displayName: field.name,
                }));
        },
        highlightedExpression() {
            return this.highlightSyntax(this.expression);
        },
        escapedHighlightedExpression() {
            // Önizleme için HTML tag'lerini kaldır ve düz metin olarak göster
            return this.renderSafeHTML(this.highlightSyntax(this.expression));
        },
    },
    watch: {
        value(newValue) {
            this.expression = newValue;
            this.updateEditorContent();
        },
        expression(newValue) {
            this.$emit("input", newValue);
            this.validateExpression();
        },
    },
    created() {
        // Tüm fonksiyonları birleştir
        this.allFunctions = [];
        this.functionCategories.forEach((category) => {
            this.allFunctions = [...this.allFunctions, ...category.functions];
        });
    },
    mounted() {
        this.updateEditorContent();
        document.addEventListener("click", this.handleDocumentClick);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleDocumentClick);
    },
    methods: {
        // Editor methods
        updateEditorContent() {
            if (this.$refs.editorInput) {
                try {
                    // Önceki seçim ve cursor pozisyonunu sakla
                    const selection = window.getSelection();
                    let cursorPos = this.cursorPosition;

                    if (selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        cursorPos = range.startOffset;
                    }

                    // Input alanına sadece düz metin olarak içeriği ayarla - HTML kullanma
                    this.$refs.editorInput.textContent = this.expression;

                    // Eski cursor pozisyonunu geri yükle
                    this.$nextTick(() => {
                        try {
                            // Cursor'ı düz metin içinde pozisyonlandır
                            if (selection && selection.rangeCount > 0) {
                                const range = document.createRange();
                                const textNode = this.$refs.editorInput.firstChild || this.$refs.editorInput;
                                const pos = Math.min(cursorPos, this.expression.length);

                                if (textNode.nodeType === Node.TEXT_NODE) {
                                    range.setStart(textNode, pos);
                                    range.collapse(true);
                                    selection.removeAllRanges();
                                    selection.addRange(range);
                                } else {
                                    this.setFocus();
                                }
                            }
                        } catch (e) {
                            console.error("Cursor pozisyonu ayarlanamadı:", e);
                        }
                    });
                } catch (e) {
                    console.error("Editor içeriği güncellenirken hata:", e);

                    // Hata durumunda düz metni ayarla
                    this.$refs.editorInput.textContent = this.expression;
                }
            }
        },
        handleEditorInput(e) {
            try {
                // contenteditable alanından düz metni al
                const plainText = this.$refs.editorInput.textContent || "";

                // Metin içeriğini expression'a aktar
                this.expression = plainText;

                // Cursor pozisyonunu al
                const selection = window.getSelection();
                if (selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    this.cursorPosition = range.startOffset;

                    // Son kelimeyi bulmak için cursor'un öncesindeki metni al
                    const textBeforeCursor = plainText.substring(0, this.cursorPosition);
                    const words = textBeforeCursor.split(/[\s\(\),]/);
                    this.lastWord = words[words.length - 1] || "";

                    this.showAutocompleteSuggestions();
                }
            } catch (e) {
                console.error("Editor input işlenirken hata:", e);
            }
        },
        handleKeyUp(e) {
            // Sadece belirli tuşlar autocomplete'i tetiklesin
            const key = e.key;
            if (key !== "ArrowUp" && key !== "ArrowDown" && key !== "Enter" && key !== "Escape") {
                // Cursor pozisyonunu al
                const selection = window.getSelection();
                if (selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    this.cursorPosition = range.startOffset;

                    // Son kelimeyi bulmak için cursor'un öncesindeki metni al
                    const textBeforeCursor = this.$refs.editorInput.textContent.substring(0, this.cursorPosition);
                    const words = textBeforeCursor.split(/[\s\(\),]/);
                    this.lastWord = words[words.length - 1];

                    this.showAutocompleteSuggestions();
                }
            }
        },
        handleKeyDown(e) {
            const key = e.key;

            // Enter tuşu özel olarak ele alınmalı (autocomplete için değilse)
            if (key === "Enter" && !this.showAutocomplete) {
                e.preventDefault(); // Enter tuşunun div içinde yeni satır eklemesini engelle
                return;
            }

            // Önce autocomplete'i kontrol edelim
            if (this.showAutocomplete) {
                if (key === "ArrowDown") {
                    e.preventDefault();
                    this.autocompleteSelectedIndex = Math.min(
                        this.autocompleteSelectedIndex + 1,
                        this.autocompleteItems.length - 1
                    );
                } else if (key === "ArrowUp") {
                    e.preventDefault();
                    this.autocompleteSelectedIndex = Math.max(0, this.autocompleteSelectedIndex - 1);
                } else if ((key === "Enter" || key === "Tab") && this.autocompleteItems.length > 0) {
                    e.preventDefault();
                    this.insertAutocompleteItem(this.autocompleteItems[this.autocompleteSelectedIndex]);
                } else if (key === "Escape") {
                    e.preventDefault();
                    this.hideAutocomplete();
                }
            } else {
                // Parantez, virgül veya özel karakter yazıldığında mevcut içeriği güncelle
                if (["{", "}", "(", ")", ",", "+", "-", "*", "/", ">", "<", "=", "%"].includes(key)) {
                    // Devam et, normal karakter girişi olarak ele alacağız
                    // Bu karakterleri yazdıktan sonra editör içeriğini güvenli şekilde güncelleyeceğiz
                    this.$nextTick(() => {
                        // İçeriği al ve ifadeyi güncelle
                        const plainText = this.$refs.editorInput.textContent;
                        this.expression = plainText;

                        // Özel karakterden sonra autocomplete önerilerini göster/gizle
                        if (key === "{") {
                            this.showFieldSuggestions();
                        } else if (key === "(") {
                            this.hideAutocomplete();
                        }
                    });
                }
            }
        },
        setFocus() {
            if (!this.$refs.editorInput) return;

            this.$refs.editorInput.focus();

            // Cursor pozisyonunu en sona ayarla
            try {
                const range = document.createRange();
                const sel = window.getSelection();
                const node = this.$refs.editorInput;

                if (node.childNodes.length === 0) {
                    // Eğer içerik boşsa, bir text node ekle
                    const textNode = document.createTextNode("");
                    node.appendChild(textNode);
                    range.setStart(textNode, 0);
                } else {
                    // Son node'u bul ve sonuna atla
                    const lastNode = (function findLastTextNode(node) {
                        if (node.nodeType === Node.TEXT_NODE) return node;
                        if (node.lastChild) return findLastTextNode(node.lastChild);
                        return node;
                    })(node);

                    if (lastNode.nodeType === Node.TEXT_NODE) {
                        range.setStart(lastNode, lastNode.length);
                    } else {
                        range.setStart(lastNode, 0);
                    }
                }

                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
            } catch (e) {
                console.error("Cursor pozisyonu en sona ayarlanamadı:", e);
            }
        },
        // Autocomplete methods
        showAutocompleteSuggestions() {
            // Bir şey yazılırken veya { yazıldıktan sonra önerileri göster
            if (this.lastWord.startsWith("{")) {
                // Field önerilerini göster
                this.showFieldSuggestions();
            } else if (this.lastWord.length >= 1 && /^[a-zA-Z]+$/.test(this.lastWord)) {
                // Fonksiyon önerilerini göster (harf içeren sözcükler için)
                // Küçük harfle de çalışacak
                this.showFunctionSuggestions();
            } else {
                this.hideAutocomplete();
            }
        },
        showFieldSuggestions() {
            const query = this.lastWord.replace(/^{/, "").toLowerCase();

            let filteredFields = this.availableFields;
            if (query) {
                filteredFields = this.availableFields.filter((field) => field.name.toLowerCase().includes(query));
            }

            if (filteredFields.length > 0) {
                this.autocompleteType = "field";
                this.autocompleteItems = filteredFields;
                this.autocompleteTitle = "Alanlar";
                this.autocompleteSelectedIndex = 0;
                this.showAutocomplete = true;
                this.positionAutocomplete();
            } else {
                this.hideAutocomplete();
            }
        },
        showFunctionSuggestions() {
            const query = this.lastWord.toUpperCase();

            // Fonksiyonları filtrele (başlangıca eşleşenler önce gelsin)
            const exactMatches = this.allFunctions.filter((func) => func.name.toUpperCase().startsWith(query));
            const partialMatches = this.allFunctions.filter(
                (func) => func.name.toUpperCase().includes(query) && !func.name.toUpperCase().startsWith(query)
            );

            const filteredFunctions = [...exactMatches, ...partialMatches];

            if (filteredFunctions.length > 0) {
                this.autocompleteType = "function";
                this.autocompleteItems = filteredFunctions;
                this.autocompleteTitle = "Fonksiyonlar";
                this.autocompleteSelectedIndex = 0;
                this.showAutocomplete = true;
                this.positionAutocomplete();
            } else {
                this.hideAutocomplete();
            }
        },
        positionAutocomplete() {
            this.$nextTick(() => {
                const selection = window.getSelection();
                if (selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    const rect = range.getBoundingClientRect();
                    const editorRect = this.$refs.editorContainer.getBoundingClientRect();

                    // Pozisyonu hesapla
                    let top = rect.bottom - editorRect.top + 5;
                    let left = rect.left - editorRect.left;

                    // Dropdown'ın taşmasını önle
                    const dropdownWidth = 300;
                    if (left + dropdownWidth > editorRect.width) {
                        left = editorRect.width - dropdownWidth;
                    }

                    this.autocompletePosition = {
                        top: `${top}px`,
                        left: `${left}px`,
                    };
                }
            });
        },
        hideAutocomplete() {
            this.showAutocomplete = false;
        },
        insertAutocompleteItem(item) {
            try {
                const selection = window.getSelection();
                if (!selection.rangeCount) {
                    this.setFocus();
                    return;
                }

                // Düz metin içeriğini al
                const text = this.$refs.editorInput.textContent || "";

                // Son kelimeyi sil ve yenisiyle değiştir
                const startPosition = Math.max(0, this.cursorPosition - this.lastWord.length);
                const endPosition = this.cursorPosition;

                let insertText = "";
                let cursorOffset = 0;

                if (this.autocompleteType === "function") {
                    insertText = `${item.name}()`;
                    cursorOffset = 1; // İmleci parantezin içine yerleştir
                } else if (this.autocompleteType === "field") {
                    insertText = `{${item._id}}`;
                    cursorOffset = 0; // İmleci alan referansının sonuna yerleştir
                }

                // Metni düzgün şekilde güncelle
                const newText = text.substring(0, startPosition) + insertText + text.substring(endPosition);
                this.expression = newText;

                // Autocomplete'i gizle
                this.hideAutocomplete();

                // İçeriği güncelle
                this.$nextTick(() => {
                    // Düz metin olarak içeriği güncelle
                    this.$refs.editorInput.textContent = this.expression;

                    // Cursor pozisyonunu yeniden belirle
                    setTimeout(() => {
                        const newCursorPosition = startPosition + insertText.length - cursorOffset;

                        const range = document.createRange();
                        const textNode = this.$refs.editorInput.firstChild || this.$refs.editorInput;

                        if (textNode.nodeType === Node.TEXT_NODE) {
                            range.setStart(textNode, newCursorPosition);
                            range.collapse(true);
                            selection.removeAllRanges();
                            selection.addRange(range);
                        }

                        this.$refs.editorInput.focus();
                    }, 10);
                });
            } catch (error) {
                console.error("Autocomplete öğesi eklenirken hata:", error);
            }
        },
        setCursorPosition(position) {
            const editorNode = this.$refs.editorInput;
            if (!editorNode) return;

            try {
                const textNode = editorNode.firstChild || editorNode;
                if (textNode.nodeType !== Node.TEXT_NODE) {
                    const newTextNode = document.createTextNode(this.expression);
                    editorNode.innerHTML = "";
                    editorNode.appendChild(newTextNode);
                    textNode = newTextNode;
                }

                const range = document.createRange();
                const selection = window.getSelection();

                // Pozisyonu sınırla
                position = Math.min(position, textNode.length);
                position = Math.max(position, 0);

                range.setStart(textNode, position);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
            } catch (e) {
                console.error("setCursorPosition hatası:", e);
            }
        },
        handleDocumentClick(e) {
            // Tıklama editör dışına olursa autocomplete'i kapat
            if (this.$refs.editorContainer && !this.$refs.editorContainer.contains(e.target)) {
                this.hideAutocomplete();
            }
        },
        // Content handling
        handleInput() {
            this.validateExpression();
        },
        validateExpression() {
            this.error = null;

            if (!this.expression) {
                return;
            }

            // Basit parantez kontrolü
            const openParens = (this.expression.match(/\(/g) || []).length;
            const closeParens = (this.expression.match(/\)/g) || []).length;

            if (openParens !== closeParens) {
                this.error = "Parantez açma-kapama eşleşmiyor";
                return;
            }

            // Formül sözdizimini kontrol et
            if (!this.expression.match(/^[A-Z]+\([^)]*\)/) && this.expression.includes("(")) {
                this.error = "Geçersiz formül formatı";
                return;
            }

            // Referans edilen alanların kontrolü
            const fieldRefPattern = /{([^}]*)}/g;
            let match;

            while ((match = fieldRefPattern.exec(this.expression)) !== null) {
                const fieldId = match[1];
                const field = this.allFields.find((f) => f._id === fieldId);

                if (!field) {
                    this.error = `"${fieldId}" adlı alan bulunamadı`;
                    return;
                }
            }
        },

        insertField(field) {
            const fieldRef = `{${field._id}}`;

            try {
                const selection = window.getSelection();

                if (selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    const text = this.$refs.editorInput.textContent || "";
                    const cursorPosition = range.startOffset;

                    // Metni güncelle
                    const newText = text.substring(0, cursorPosition) + fieldRef + text.substring(cursorPosition);
                    this.expression = newText;

                    // Düz metin olarak içeriği güncelle
                    this.$nextTick(() => {
                        this.$refs.editorInput.textContent = this.expression;

                        setTimeout(() => {
                            const range = document.createRange();
                            const textNode = this.$refs.editorInput.firstChild || this.$refs.editorInput;

                            if (textNode.nodeType === Node.TEXT_NODE) {
                                range.setStart(textNode, cursorPosition + fieldRef.length);
                                range.collapse(true);
                                selection.removeAllRanges();
                                selection.addRange(range);
                            }

                            this.$refs.editorInput.focus();
                        }, 10);
                    });
                } else {
                    // İmleç yoksa, sona ekle
                    this.expression += fieldRef;

                    this.$nextTick(() => {
                        this.$refs.editorInput.textContent = this.expression;
                        this.setFocus();
                    });
                }
            } catch (error) {
                console.error("Alan eklenirken hata:", error);
            }
        },
        insertFunction(func) {
            const funcTemplate = `${func.name}()`;

            try {
                const selection = window.getSelection();

                if (selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    const text = this.$refs.editorInput.textContent || "";
                    const cursorPosition = range.startOffset;

                    // Metni güncelle
                    const newText = text.substring(0, cursorPosition) + funcTemplate + text.substring(cursorPosition);
                    this.expression = newText;

                    // Düz metin olarak içeriği güncelle
                    this.$nextTick(() => {
                        this.$refs.editorInput.textContent = this.expression;

                        setTimeout(() => {
                            const range = document.createRange();
                            const textNode = this.$refs.editorInput.firstChild || this.$refs.editorInput;

                            if (textNode.nodeType === Node.TEXT_NODE) {
                                range.setStart(textNode, cursorPosition + func.name.length + 1);
                                range.collapse(true);
                                selection.removeAllRanges();
                                selection.addRange(range);
                            }

                            this.$refs.editorInput.focus();
                        }, 10);
                    });
                } else {
                    // İmleç yoksa, sona ekle
                    this.expression += funcTemplate;

                    this.$nextTick(() => {
                        this.$refs.editorInput.textContent = this.expression;

                        setTimeout(() => {
                            const textNode = this.$refs.editorInput.firstChild || this.$refs.editorInput;

                            if (textNode.nodeType === Node.TEXT_NODE) {
                                const range = document.createRange();
                                range.setStart(textNode, this.expression.length - 1);
                                range.collapse(true);
                                selection.removeAllRanges();
                                selection.addRange(range);
                            }

                            this.$refs.editorInput.focus();
                        }, 10);
                    });
                }
            } catch (error) {
                console.error("Fonksiyon eklenirken hata:", error);
            }
        },
        useExample(exampleFormula) {
            // Örnekteki alan ID'lerini ekrandaki gerçek mevcut alanlarla değiştir
            let modifiedFormula = exampleFormula;

            // Basit bir alan ID değiştirme algoritması
            // Gerçek uygulamada daha karmaşık bir mantık kullanılabilir
            const fieldIdPattern = /{field_id_(\d+)}/g;
            const availableFields = this.availableFields;

            if (availableFields && availableFields.length > 0) {
                modifiedFormula = exampleFormula.replace(fieldIdPattern, (match, index) => {
                    // Eğer mevcut alanlar varsa, sırayla kullan
                    const fieldIndex = parseInt(index) - 1;
                    if (fieldIndex >= 0 && fieldIndex < availableFields.length) {
                        return `{${availableFields[fieldIndex]._id}}`;
                    }
                    // Eğer index dışındaysa, ilk alanı kullan
                    return `{${availableFields[0]._id}}`;
                });
            }

            this.expression = modifiedFormula;
            this.$nextTick(() => {
                this.updateEditorContent();
                this.setFocus();
            });
        },
        renderSafeHTML(html) {
            // HTML string'lerini güvenli bir şekilde render etmek için
            const div = document.createElement("div");
            div.innerHTML = html;
            return div.textContent || div.innerText || "";
        },
        highlightSyntax(text) {
            if (!text) return "";

            try {
                // Önce potansiyel olarak tehlikeli HTML'leri escape edelim
                const escapedText = text
                    .replace(/&/g, "&amp;")
                    .replace(/</g, "&lt;")
                    .replace(/>/g, "&gt;")
                    .replace(/"/g, "&quot;");

                // Fonksiyonları vurgula
                let highlighted = escapedText.replace(/([A-Z]+)(\()/g, '<span class="syntax-function">$1</span>$2');

                // Alan referanslarını vurgula - field_id yerine field_name göster
                highlighted = highlighted.replace(/{([^}]*)}/g, (match, fieldId) => {
                    const field = this.allFields.find((f) => f._id === fieldId);
                    if (field) {
                        return `<span class="syntax-field">{${field.name}}</span>`;
                    }

                    // field_id_1 gibi örneklerdeki alanları tanımlı göster
                    if (fieldId.startsWith("field_id_")) {
                        return `<span class="syntax-field-unknown">{${fieldId}}</span>`;
                    }

                    return `<span class="syntax-field-unknown">{${fieldId}}</span>`;
                });

                // Sayıları vurgula
                highlighted = highlighted.replace(/(\s|^)(\d+(\.\d+)?)/g, '$1<span class="syntax-number">$2</span>');

                // Operatörleri vurgula
                highlighted = highlighted.replace(/([+\-*\/><=%])/g, '<span class="syntax-operator">$1</span>');

                // String literallerini vurgula
                highlighted = highlighted.replace(
                    /(&quot;)([^&]*)(&quot;)/g,
                    '<span class="syntax-string">&quot;$2&quot;</span>'
                );

                return highlighted;
            } catch (e) {
                console.error("Sözdizimi vurgulama hatası:", e);
                return text; // Hata durumunda orijinal metni döndür
            }
        },
        getFieldIcon(fieldType) {
            switch (fieldType) {
                case "text":
                case "long-text":
                    return "text_fields";
                case "number":
                    return "looks_one";
                case "currency":
                    return "attach_money";
                case "datetime":
                    return "event";
                case "checkbox":
                    return "check_box";
                case "formula":
                    return "functions";
                default:
                    return "help_outline";
            }
        },
        getFieldTypeLabel(fieldType) {
            const typeMap = {
                text: "Metin",
                "long-text": "Uzun Metin",
                number: "Sayı",
                currency: "Para Birimi",
                datetime: "Tarih/Saat",
                checkbox: "Onay Kutusu",
                dropdown: "Açılır Liste",
                url: "URL",
                email: "E-posta",
                phone: "Telefon",
                json: "JSON",
                file: "Dosya",
                image: "Görsel",
                video: "Video",
                audio: "Ses",
                color: "Renk",
            };

            return typeMap[fieldType] || fieldType;
        },
        handleBlur() {
            // Editor blur olduğunda autocomplete'i gizle
            setTimeout(() => {
                this.hideAutocomplete();
            }, 200); // Tıklama işleminin gerçekleşmesine izin vermek için küçük bir gecikme
        },
        handlePaste(e) {
            // Yapıştırma olayını yakala ve ham metin olarak ekle
            e.preventDefault();

            // Clipboard verilerini al
            const text = e.clipboardData.getData("text/plain");

            // Mevcut imleç pozisyonunda metni ekle
            document.execCommand("insertText", false, text);

            // İçeriği güncelle
            this.$nextTick(() => {
                const plainText = this.$refs.editorInput.textContent || "";
                this.expression = plainText;
            });
        },
        // AI Assistant methods
        showAiAssistant() {
            this.aiAssistantVisible = true;
        },

        useGeneratedFormula(formula) {
            if (!formula) return;

            // Formülü ifadeye ekle
            this.expression = formula;

            // Modal'ı kapat
            this.aiAssistantVisible = false;

            // Editörü güncelle ve focusla
            this.$nextTick(() => {
                this.updateEditorContent();
                this.setFocus();
            });

            this.$message.success("Formül başarıyla eklendi!");
        },

        resetAiAssistant() {
            this.aiResult = null;
            this.customerPrompt = "";
        },
    },
});
</script>

<style lang="scss">
/* Scoped olmayan global stilller */
.syntax-function {
    color: #1890ff !important;
    font-weight: bold !important;
}

.syntax-field {
    color: #722ed1 !important;
    background-color: #f9f0ff !important;
    padding: 0 2px !important;
    border-radius: 2px !important;
}

.syntax-field-unknown {
    color: #f5222d !important;
    background-color: #fff1f0 !important;
    padding: 0 2px !important;
    border-radius: 2px !important;
}

.syntax-number {
    color: #fa8c16 !important;
}

.syntax-operator {
    color: #eb2f96 !important;
    font-weight: bold !important;
}

.syntax-string {
    color: #52c41a !important;
}
</style>

<style lang="scss" scoped>
.formula-field-editor {
    width: 100%;

    .editor-container {
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 10px;
        margin-bottom: 20px;

        .toolbar {
            display: flex;
            gap: 10px;
            margin-bottom: 10px;

            .insert-field,
            .insert-function,
            .ai-assistant {
                i {
                    font-size: 16px;
                    vertical-align: middle;
                    margin-right: 5px;
                }
            }
        }

        .editor {
            margin-bottom: 10px;
            position: relative;

            .formula-input-container {
                position: relative;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                padding: 4px 11px;
                font-size: 14px;
                min-height: 100px;
                background-color: #fff;

                &:focus-within {
                    border-color: #40a9ff;
                    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
                }

                .formula-input {
                    outline: none;
                    min-height: 100px;
                    font-family: "Courier New", monospace;
                    line-height: 1.6;
                    padding: 4px 0;
                    width: 100%;
                    white-space: pre-wrap;
                    word-break: break-all;

                    &:focus {
                        outline: none;
                    }

                    ::v-deep .syntax-function {
                        color: #1890ff;
                        font-weight: bold;
                    }

                    ::v-deep .syntax-field {
                        color: #722ed1;
                        background-color: #f9f0ff;
                        padding: 0 2px;
                        border-radius: 2px;
                    }

                    ::v-deep .syntax-field-unknown {
                        color: #f5222d;
                        background-color: #fff1f0;
                        padding: 0 2px;
                        border-radius: 2px;
                    }

                    ::v-deep .syntax-number {
                        color: #fa8c16;
                    }

                    ::v-deep .syntax-operator {
                        color: #eb2f96;
                        font-weight: bold;
                    }

                    ::v-deep .syntax-string {
                        color: #52c41a;
                    }
                }
            }

            .autocomplete-dropdown {
                position: absolute;
                z-index: 1050;
                background: white;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                width: 300px;
                max-height: 300px;
                overflow-y: auto;

                .autocomplete-title {
                    padding: 8px 12px;
                    font-weight: bold;
                    background-color: #f5f5f5;
                    border-bottom: 1px solid #d9d9d9;
                }

                .autocomplete-items {
                    .autocomplete-item {
                        padding: 8px 12px;
                        display: flex;
                        align-items: flex-start;
                        cursor: pointer;
                        border-bottom: 1px solid #f0f0f0;

                        &:hover,
                        &.active {
                            background-color: #e6f7ff;
                        }

                        &:last-child {
                            border-bottom: none;
                        }

                        i {
                            margin-right: 8px;
                            margin-top: 2px;
                            font-size: 16px;
                            color: #1890ff;
                        }

                        .autocomplete-item-details {
                            flex: 1;

                            .item-name {
                                font-weight: bold;
                                color: #000;
                            }

                            .item-description {
                                font-size: 12px;
                                color: #666;
                                margin-top: 2px;
                            }

                            .item-syntax {
                                font-family: "Courier New", monospace;
                                font-size: 11px;
                                color: #1890ff;
                                background-color: #f0f8ff;
                                padding: 2px 4px;
                                border-radius: 2px;
                                margin-top: 2px;
                            }
                        }
                    }
                }
            }
        }

        .syntax-help {
            min-height: 40px;
            padding: 8px;
            background-color: #f9f9f9;
            border-radius: 4px;

            .error-message {
                color: #f5222d;
                margin: 0;
            }

            .preview-message {
                margin: 0;

                .preview-label {
                    font-weight: bold;
                    margin-right: 5px;
                }

                ::v-deep .highlighted-expression {
                    font-family: "Courier New", monospace;
                }
            }

            .help-message {
                color: #666;
                margin: 0;
            }
        }
    }

    .examples-section {
        margin-top: 20px;

        .examples {
            display: flex;
            flex-direction: column;
            gap: 15px;

            .example {
                border: 1px solid #e8e8e8;
                border-radius: 4px;
                padding: 10px;

                .example-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 5px;

                    .example-name {
                        font-weight: bold;
                    }

                    i {
                        font-size: 14px;
                        vertical-align: middle;
                    }
                }

                .example-formula {
                    font-family: "Courier New", monospace;
                    background-color: #f5f5f5;
                    padding: 5px;
                    border-radius: 4px;
                    margin-bottom: 5px;
                    overflow-x: auto; /* Uzun formüller için kaydırma çubuğu */

                    ::v-deep .syntax-function {
                        color: #1890ff;
                        font-weight: bold;
                    }

                    ::v-deep .syntax-field {
                        color: #722ed1;
                        background-color: #f9f0ff;
                        padding: 0 2px;
                        border-radius: 2px;
                    }

                    ::v-deep .syntax-field-unknown {
                        color: #f5222d;
                        background-color: #fff1f0;
                        padding: 0 2px;
                        border-radius: 2px;
                    }

                    ::v-deep .syntax-number {
                        color: #fa8c16;
                    }

                    ::v-deep .syntax-operator {
                        color: #eb2f96;
                        font-weight: bold;
                    }

                    ::v-deep .syntax-string {
                        color: #52c41a;
                    }
                }

                .example-description {
                    color: #666;
                    font-size: 12px;
                }
            }
        }
    }
}

::v-deep .syntax-function {
    color: #1890ff;
    font-weight: bold;
}

::v-deep .syntax-field {
    color: #722ed1;
    background-color: #f9f0ff;
    padding: 0 2px;
    border-radius: 2px;
}

::v-deep .syntax-field-unknown {
    color: #f5222d;
    background-color: #fff1f0;
    padding: 0 2px;
    border-radius: 2px;
}

::v-deep .syntax-number {
    color: #fa8c16;
}

::v-deep .syntax-operator {
    color: #eb2f96;
    font-weight: bold;
}

::v-deep .syntax-string {
    color: #52c41a;
}

::v-deep .function-menu {
    max-height: 400px;
    overflow-y: auto;

    .function-item {
        display: flex;
        flex-direction: column;

        .function-name {
            font-weight: bold;
        }

        .function-desc {
            font-size: 12px;
            color: #666;
        }
    }
}

.ai-assistant-modal {
    .prompt-container {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .assistant-buttons {
            display: flex;
            justify-content: center;
            margin-top: 16px;
        }

        .example-prompts {
            margin-top: 24px;

            h4 {
                margin-bottom: 12px;
                text-align: center;
            }

            .prompt-cards {
                display: flex;
                flex-direction: column;
                gap: 12px;

                .prompt-card {
                    padding: 12px;
                    border: 1px solid #e8e8e8;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.3s;
                    display: flex;
                    align-items: flex-start;

                    &:hover {
                        background-color: #f5f5f5;
                        transform: translateY(-2px);
                    }

                    .anticon {
                        color: #1890ff;
                        margin-right: 8px;
                        margin-top: 3px;
                    }

                    p {
                        margin: 0;
                    }
                }
            }
        }
    }

    .result-container {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .result-header {
            text-align: center;
            margin-bottom: 8px;

            h3 {
                margin: 0;
                color: #1890ff;
            }
        }

        .result-formula {
            background: #f5f5f5;
            padding: 16px;
            border-radius: 4px;
            border-left: 4px solid #1890ff;

            pre {
                margin: 0;
                white-space: pre-wrap;
                word-break: break-word;
                font-family: "Courier New", monospace;
            }
        }

        .result-explanation {
            padding: 0 8px;

            p {
                margin: 0;
                color: #666;
            }
        }

        .result-buttons {
            display: flex;
            justify-content: center;
            gap: 12px;
            margin-top: 16px;
        }
    }
}

.ai-assistant {
    margin-left: auto;

    .ai-suggest-button {
        display: flex;
        align-items: center;
        //background-color: transparent;
        //border: none;
        box-shadow: none;
        color: rgba(0, 0, 0, 0.65);
        padding: 8px 12px;
        border-radius: 4px;
        transition: all 0.3s;

        &:hover {
            background-color: #f0f5ff;
            color: #1890ff;
        }

        i {
            font-size: 18px;
            margin-right: 8px;
        }

        span {
            font-size: 14px;
        }
    }
}

.copilot-icon {
    vertical-align: middle;
    margin-right: 5px;
    width: 15px;
    height: 15px;
}

@keyframes wave {
    0%,
    100% {
        transform: scaleY(0.5);
    }
    50% {
        transform: scaleY(1);
    }
}
</style>
